import React, { useEffect, useMemo, useState } from 'react';
import { Box, Flex, Link, Image } from '@chakra-ui/react';
import { numberFormat } from 'utils/mathCommon';
import NumberFormat from 'react-number-format';
import { useSelector } from 'react-redux';
import { parseBalance, bnum } from 'utils/helper';
import AlertHashTransaction from 'Components/AlertHashTransaction';
import { notification } from 'utils/notification';
import TimeCounter from 'Components/TimeCounter';
import ERC20 from 'Contracts/ERC20.json';
import styled from 'styled-components';
import { Button } from 'antd';
import {UNLIMITED_WANA} from '__mocks__/PackageData';

const PACKAGE_SILVER = 1;
const PACKAGE_GOLD = 2;
const PACKAGE_RUBY = 3;
const PACKAGE_DIAMOND = 4;
const PACKAGE_LP_RUBY = 5;
const OPTION_7_DAYS = 1;
const OPTION_30_DAYS = 2;
const OPTION_90_DAYS = 3;
const OPTION_180_DAYS = 4;

const StyledPackedDetail = styled.div`
  padding: 20px 20px;
  background-color: var(--card-body);
  margin: 0;
`;

const StyledPoolLimit = styled.div`
  text-align: right;
  max-width: 230px;
  width: 100%;
  @media screen and (max-width: 500px) {
    max-width: 160px;
  }
`

const StyledLimitTitle = styled.div`
  font-size: 12px;
  font-weight: 900;
  color: #d4f0ff;
  font-family: ChakraPetch;
  text-transform: uppercase;
`

const StyledNumberFormat = styled(NumberFormat)`
  width: 100%;
  height: 30px;
  padding: 0 11px;
  text-align: left;
  background-color: var(--background-color);
  border: 0;
  border-radius: 2px;
  outline: 0;
  transition: all 0.3s linear;
  -webkit-appearance: textfield !important;
  -moz-appearance: textfield !important;
  appearance: textfield !important;
  margin-right: 20px;
`
const StyledContentDetail = styled.div`
  display: grid;
  grid-gap: 32px;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  margin-top: 20px;
  @media screen and (max-width: 375px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
`;

const StyledStats = styled.div`
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  background: #00aeef; /* fallback for old browsers */
  background: -webkit-linear-gradient(to right, #00aeef, #2b3990); /* Chrome 10-25, Safari 5.1-6 */
  background: linear-gradient(to right, #00aeef, #2b3990); /* W3C, IE 10+/ Edge, Firefox 16+, Chrome 26+, Opera 12+, Safari 7+ */
`

export const StyledDetailInfo = styled.div`
  color: #e1e1e1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 5px;
  .value {
    color: #fff;
  }
`

const StyledTitleStats = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #d4f0ff;
  font-family: ChakraPetch;
  text-transform: uppercase;
`

const StyledTitleDetail = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #f7f7f7;
  text-align: left;
`;

const StyledBuyWana = styled.div`
  color: #21c186;
  margin: 0 5px;
`
const StyledLeft = styled.div``;
const StyledRight = styled.div`
  .btn-stake {
    margin-top: 1rem;
  }
`;

const StyledErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 13px;
  margin-top: 10px;
  font-weight: 600;
`;

export default function StakeDetail({ option, packageItem, rootUrlsView, fetchDetailPool, fetchTotalValue }) {
  const [isLoading, setIsLoading] = useState(false);
  const { web3, walletAddress, chainId } = useSelector((state) => state);
  const [transactionInfo, setTransactionInfo] = useState({
    transactionHash: '',
    status: false,
  });
  const [allowanceAmount, setAllowanceAmount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [disableConfirm, setDisableConfirm] = useState(false);
  const [stakeAmount, setStakeAmount] = useState(packageItem.minStake);
  const [totalStakedByOption, setTotalStakedByOption] = useState(0);
  const [stakedPool, setStakedPool] = useState([]);

  const poolLimit = useMemo(() => {
    return option?.poolLimit === UNLIMITED_WANA
      ? 'Unlimited'
      : `${numberFormat(option.poolLimit)} ${packageItem.tokenName}`;
  }, [option.poolLimit, packageItem.tokenName]);


  const percentPool = useMemo(() => {
    return option?.poolLimit !== UNLIMITED_WANA
      ? (totalStakedByOption / option.poolLimit) * 100
      : 0;
  }, [option, totalStakedByOption]);

  const getAllowanceAmount = async () => {
    try {
      const instanceErc20 = new web3.eth.Contract(ERC20.abi, packageItem.contractToken);

      await instanceErc20.methods
        .allowance(walletAddress, packageItem.contractFarm)
        .call((err, res) => {
          if (err) {
            notification('Get amount allowance fail', 'error');
            return;
          }
          if (res) {
            setAllowanceAmount(Number(parseBalance(res, 18)));
          }
        });
    } catch (error) {
      console.log(error);
    }
  };

  const getStakedPool = async () => {
    try {
      const stakingContract = new web3.eth.Contract(
        packageItem.abi,
        packageItem.contractFarm
      );

      await stakingContract.methods.getDetailStakedPool().call((err, res) => {
        if (err) {
          console.log(err);
          return;
        }
        setStakedPool(res);
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!walletAddress) return;
    getAllowanceAmount();
    getStakedPool();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletAddress, chainId]);

  const handleStakeAmount = (values) => {
    const { value } = values;
    setStakeAmount(value ? parseFloat(value) : 0);

    setErrorMessage('');
    if (parseFloat(value) > packageItem.maxStake) {
      setErrorMessage(
        `The stake amount can not be higher than ${numberFormat(
          packageItem.maxStake
        )} maximum ${packageItem.tokenName}`
      );
      return;
    }

    if (parseFloat(value) > packageItem.balanceLP) {
      setErrorMessage('The stake amount can not be higher than the available amount');
      return;
    }

    if (parseFloat(value) < packageItem.minStake) {
      setErrorMessage(
        `The stake amount can not be lower than ${numberFormat(packageItem.minStake)} WANA`
      );
      return;
    }
  };

  const handleMaxWANA = () => {
    if (bnum(packageItem.balanceLP).isGreaterThanOrEqualTo((bnum(packageItem.maxStake).multipliedBy(1e18)))) {
      setStakeAmount(packageItem.maxStake);
    } else {
      setStakeAmount(bnum(packageItem.balanceLP).dividedBy(1e18).toFixed());
    }
  };

  const approveContract = async () => {
    if (packageItem.balanceLP < packageItem.minStake) {
      notification('Your WANA balance is insufficient to approve ', 'error');
      return;
    }

    try {
      const wanakaFarmContract = new web3.eth.Contract(
        ERC20.abi,
        packageItem.contractToken
      );

      return wanakaFarmContract.methods
        .approve(
          packageItem.contractFarm,
          web3.utils.toWei(packageItem.balanceLP.toString(), 'ether')
        )
        .send({
          from: walletAddress,
        })
        .on('receipt', (receipt) => {
          if (receipt.status) {
            setTransactionInfo({
              transactionHash: receipt?.transactionHash,
              status: true,
            });
            getAllowanceAmount();
            notification('An Approve has been successfully', 'success');
          } else {
            if (receipt?.transactionHash) {
              setTransactionInfo({
                transactionHash: receipt?.transactionHash,
                status: false,
              });
            }
            notification('An approval has failed', 'error');
          }
        })
        .on('error', (error, receipt) => {
          if (receipt?.transactionHash) {
            setTransactionInfo({
              transactionHash: receipt?.transactionHash,
              status: false,
            });
          }
          notification('An approval has failed', 'error');
        });
    } catch (error) {
      console.log(error);
      throw error
    }
  };

  const updateSuccessfully = () => {
    notification('Congratulation, you have staked successfully', 'success');
    setStakeAmount(packageItem.minStake);
    getStakedPool();
    getAllowanceAmount();
    fetchDetailPool();
    fetchTotalValue();
    setIsLoading(false);
  };

  const depositStaking = async () => {
    const stakingContract = new web3.eth.Contract(
      packageItem.abi,
      packageItem?.contractFarm
    );

    if (option.id === OPTION_7_DAYS) {
      await stakingContract.methods
        .oneWeekStake(web3.utils.toWei(stakeAmount.toString(), 'ether'))
        .send({ from: walletAddress })
        .on('receipt', async (receipt) => {
          if (receipt.status) {
            setTransactionInfo({
              transactionHash: receipt?.transactionHash,
              status: true,
            });
            updateSuccessfully(stakeAmount);
          } else {
            if (receipt?.transactionHash) {
              setTransactionInfo({
                transactionHash: receipt?.transactionHash,
                status: false,
              });
            }
            notification('The transaction is failed', 'error');
          }
        })
        .on('error', function (error, receipt) {
          if (receipt?.transactionHash) {
            setTransactionInfo({
              transactionHash: receipt?.transactionHash,
              status: false,
            });
          }
          notification('The transaction is failed', 'error');
        });
    }

    if (option.id === OPTION_30_DAYS) {
      await stakingContract.methods
        .oneMonthStake(web3.utils.toWei(stakeAmount.toString(), 'ether'))
        .send({ from: walletAddress })
        .on('receipt', async (receipt) => {
          if (receipt.status) {
            setTransactionInfo({
              transactionHash: receipt?.transactionHash,
              status: true,
            });
            updateSuccessfully(stakeAmount);
          } else {
            if (receipt?.transactionHash) {
              setTransactionInfo({
                transactionHash: receipt?.transactionHash,
                status: false,
              });
            }
            notification('The transaction is failed', 'error');
          }
        })
        .on('error', function (error, receipt) {
          if (receipt?.transactionHash) {
            setTransactionInfo({
              transactionHash: receipt?.transactionHash,
              status: false,
            });
          }
          notification('The transaction is failed', 'error');
        });
    }

    if (option.id === OPTION_90_DAYS) {
      await stakingContract.methods
        .threeMonthStake(web3.utils.toWei(stakeAmount.toString(), 'ether'))
        .send({ from: walletAddress })
        .on('receipt', async (receipt) => {
          if (receipt.status) {
            setTransactionInfo({
              transactionHash: receipt?.transactionHash,
              status: true,
            });
            updateSuccessfully(stakeAmount);
          } else {
            if (receipt?.transactionHash) {
              setTransactionInfo({
                transactionHash: receipt?.transactionHash,
                status: false,
              });
            }
            notification('The transaction is failed', 'error');
          }
        })
        .on('error', function (error, receipt) {
          if (receipt?.transactionHash) {
            setTransactionInfo({
              transactionHash: receipt?.transactionHash,
              status: false,
            });
          }
          notification('The transaction is failed', 'error');
        });
    }

    if (option.id === OPTION_180_DAYS) {
      await stakingContract.methods
        .sixMonthStake(web3.utils.toWei(stakeAmount.toString(), 'ether'))
        .send({ from: walletAddress })
        .on('receipt', async (receipt) => {
          if (receipt.status) {
            setTransactionInfo({
              transactionHash: receipt?.transactionHash,
              status: true,
            });
            updateSuccessfully(stakeAmount);
          } else {
            if (receipt?.transactionHash) {
              setTransactionInfo({
                transactionHash: receipt?.transactionHash,
                status: false,
              });
            }
            notification('The transaction is failed', 'error');
          }
        })
        .on('error', function (error, receipt) {
          if (receipt?.transactionHash) {
            setTransactionInfo({
              transactionHash: receipt?.transactionHash,
              status: false,
            });
          }
          notification('The transaction is failed', 'error');
        });
    }
  };

  const formatStakedAmount = (amount) => {
    return Number(parseBalance(amount, 18)) ?? 0;
  };

  useEffect(() => {
    getDetailStakedPool();
    setStakeAmount(packageItem.minStake);
    closeTransactionHash();

    if (!checkOverPoolLimit()) {
      setDisableConfirm(false);
      setWarningMessage('');
      return;
    }

    setDisableConfirm(true);
    setWarningMessage(
      'The Total Staked are reached the pool limit!'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageItem, option, stakedPool]);

  useEffect(() => {
    if (!checkOverPoolLimit()) {
      setDisableConfirm(false);
      setWarningMessage('');
      return;
    }

    setDisableConfirm(true);
    setWarningMessage(
      'The Total Staked packages are reached the pool limit!'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stakeAmount]);

  const getDetailStakedPool = async () => {
    if (!stakedPool || stakedPool.length === 0) {
      return;
    }
    if (option.id === OPTION_7_DAYS) {
      const option7Days = stakedPool[0];

      if (packageItem.id === PACKAGE_SILVER) {
        setTotalStakedByOption(formatStakedAmount(option7Days[0]));
      }

      if (packageItem.id === PACKAGE_GOLD) {
        setTotalStakedByOption(formatStakedAmount(option7Days[1]));
      }

      if (packageItem.id === PACKAGE_RUBY) {
        setTotalStakedByOption(formatStakedAmount(option7Days[2]));
      }

      if (packageItem.id === PACKAGE_DIAMOND) {
        setTotalStakedByOption(formatStakedAmount(option7Days[3]));
      }
      if (packageItem.id === PACKAGE_LP_RUBY) {
        setTotalStakedByOption(formatStakedAmount(option7Days[0]));
      }
    }

    if (option.id === OPTION_30_DAYS) {
      const option30Days = stakedPool[1];
      if (packageItem.id === PACKAGE_SILVER) {
        setTotalStakedByOption(formatStakedAmount(option30Days[0]));
      }

      if (packageItem.id === PACKAGE_GOLD) {
        setTotalStakedByOption(formatStakedAmount(option30Days[1]));
      }

      if (packageItem.id === PACKAGE_RUBY) {
        setTotalStakedByOption(formatStakedAmount(option30Days[2]));
      }

      if (packageItem.id === PACKAGE_DIAMOND) {
        setTotalStakedByOption(formatStakedAmount(option30Days[3]));
      }
      if (packageItem.id === PACKAGE_LP_RUBY) {
        setTotalStakedByOption(formatStakedAmount(option30Days[0]));
      }
    }

    if (option.id === OPTION_90_DAYS) {
      const option90Days = stakedPool[2];
      if (packageItem.id === PACKAGE_SILVER) {
        setTotalStakedByOption(formatStakedAmount(option90Days[0]));
      }

      if (packageItem.id === PACKAGE_GOLD) {
        setTotalStakedByOption(formatStakedAmount(option90Days[1]));
      }

      if (packageItem.id === PACKAGE_RUBY) {
        setTotalStakedByOption(formatStakedAmount(option90Days[2]));
      }

      if (packageItem.id === PACKAGE_DIAMOND) {
        setTotalStakedByOption(formatStakedAmount(option90Days[3]));
      }
      if (packageItem.id === PACKAGE_LP_RUBY) {
        setTotalStakedByOption(formatStakedAmount(option90Days[0]));
      }
    }

    if (option.id === OPTION_180_DAYS) {
      const option180Days = stakedPool[3];

      if (packageItem.id === PACKAGE_SILVER) {
        setTotalStakedByOption(formatStakedAmount(option180Days[0]));
      }

      if (packageItem.id === PACKAGE_GOLD) {
        setTotalStakedByOption(formatStakedAmount(option180Days[1]));
      }

      if (packageItem.id === PACKAGE_RUBY) {
        setTotalStakedByOption(formatStakedAmount(option180Days[2]));
      }

      if (packageItem.id === PACKAGE_DIAMOND) {
        setTotalStakedByOption(formatStakedAmount(option180Days[3]));
      }
      if (packageItem.id === PACKAGE_LP_RUBY) {
        setTotalStakedByOption(formatStakedAmount(option180Days[0]));
      }
    }
  };

  const checkApprove = () => {
    return allowanceAmount < stakeAmount;
  };

  const handleConfirmStake = async () => {
    setIsLoading(true);
    try {
      if (checkApprove()) {
        await approveContract();
      }

      await depositStaking();
    } catch (error) {
      setIsLoading(false);
    }
  };

  const calculateRate = () => {
    return (option.rate * option.duration * stakeAmount) / (365 * 100);
  };

  const checkOverPoolLimit = () => {
    let totalStakedAmountByOption = 0;
    let stakedAmountByOption = 0;

    //
    if (option.id === OPTION_7_DAYS) {
      totalStakedAmountByOption = stakedPool[0]?.reduce((sum, item) => {
        return sum + formatStakedAmount(item);
      }, 0);
    }
    if (option.id === OPTION_30_DAYS) {
      totalStakedAmountByOption = stakedPool[1]?.reduce((sum, item) => {
        return sum + formatStakedAmount(item);
      }, 0);

      if(stakedPool[1]) {
        if (packageItem.id === PACKAGE_SILVER) {
          stakedAmountByOption = stakedPool[1][0]
        } else if (packageItem.id === PACKAGE_GOLD) {
          stakedAmountByOption = stakedPool[1][1]
        } else if (packageItem.id === PACKAGE_RUBY) {
          stakedAmountByOption = stakedPool[1][2]
        } else if (packageItem.id === PACKAGE_DIAMOND) {
          stakedAmountByOption = stakedPool[1][3]
        }
      }
    }
    if (option.id === OPTION_90_DAYS) {
      totalStakedAmountByOption = stakedPool[2]?.reduce((sum, item) => {
        return sum + formatStakedAmount(item);
      }, 0);

      if (stakedPool[2]) {
        if (packageItem.id === PACKAGE_SILVER) {
          stakedAmountByOption = stakedPool[2][0]
        } else if (packageItem.id === PACKAGE_GOLD) {
          stakedAmountByOption = stakedPool[2][1]
        } else if (packageItem.id === PACKAGE_RUBY) {
          stakedAmountByOption = stakedPool[2][2]
        } else if (packageItem.id === PACKAGE_DIAMOND) {
          stakedAmountByOption = stakedPool[2][3]
        }
      }
    }
    if (option.id === OPTION_180_DAYS) {
      totalStakedAmountByOption = stakedPool[3]?.reduce((sum, item) => {
        return sum + formatStakedAmount(item);
      }, 0);

      if (stakedPool[3]) {
        if (packageItem.id === PACKAGE_SILVER) {
          stakedAmountByOption = stakedPool[3][0]
        } else if (packageItem.id === PACKAGE_GOLD) {
          stakedAmountByOption = stakedPool[3][1]
        } else if (packageItem.id === PACKAGE_RUBY) {
          stakedAmountByOption = stakedPool[3][2]
        } else if (packageItem.id === PACKAGE_DIAMOND) {
          stakedAmountByOption = stakedPool[3][3]
        }
      }
    }
    // console.log({stakedAmountByOption: bnum(stakedAmountByOption).div(1e18).toFixed(), stakeAmount, stakedPool, option: option.poolLimit })
    // console.log(stakeAmount + totalStakedAmountByOption,  option.poolLimitTotal)
    return stakeAmount + totalStakedAmountByOption > option.poolLimitTotal || bnum(stakedAmountByOption).div(1e18).plus(stakeAmount).isGreaterThan(option.poolLimit);
  };

  const closeTransactionHash = () => {
    setTransactionInfo({
      transactionHash: '',
      status: false,
    });
  };

  const checkDisabled = () => {
    return packageItem.balanceLP < packageItem.minStake;
  };

  return (
    <StyledPackedDetail padding='20px 0' maxW='670px' margin='0 auto'>
      <StyledStats
        padding='10px 20px'
        borderStyle='solid'
        borderWidth='0.6px'
        borderRadius='5px'
        background='linear-gradient(to left, rgba(3, 170, 20, 0.46), rgba(0, 174, 214, 0.12))'
      >
        <Box display='flex' justifyContent='space-between'>
          <Box>
            <StyledTitleStats>
              Total Staked
            </StyledTitleStats>
            <Box mt='6px' fontSize='20px' color='#fff200' fontWeight='700'>
              {numberFormat(Math.ceil(totalStakedByOption))} {packageItem.tokenName}
            </Box>
          </Box>
          <StyledPoolLimit>
            <StyledLimitTitle>
              Pool Limit
            </StyledLimitTitle>
            <Box
              display='flex'
              fontSize='16px'
              fontWeight='600'
              mt='5px'
              justifyContent={option?.poolLimit === UNLIMITED_WANA ? 'flex-end' : 'space-between'}
            >
              {option?.poolLimit !== UNLIMITED_WANA && <Box>{percentPool?.toFixed(2)}%</Box>}

              <Box className='amount'>{poolLimit}</Box>
            </Box>

            {option?.poolLimit !== UNLIMITED_WANA && (
              <Box
                fontSize='21px'
                lineHeight='33px'
                textTransform='uppercase'
                borderRadius='2px'
                flex='1'
                mt='10px'
                borderBottom='7px solid rgb(0, 174, 214, 0.16)'
                className='progress'
              >
                <Box
                  w={`${bnum(percentPool).gte(100) ? 100 : percentPool}%`}
                  borderRadius='3px'
                  borderBottom='7px solid #ffffff'
                  mb='-7px'
                  className='progress-detail'
                ></Box>
              </Box>
            )}
          </StyledPoolLimit>
        </Box>
        <Box mt='10px' textAlign='center' fontWeight='700'>
          {warningMessage}
        </Box>
      </StyledStats>

      <StyledContentDetail>
        <StyledLeft>
          <StyledTitleDetail>SUMMARY</StyledTitleDetail>

          <TimeCounter option={option} />

          <StyledDetailInfo>
            <div>Withdrawal Delay Time</div>
            <div className='value'>None</div>
          </StyledDetailInfo>

          <Box border='1px solid #2ea8f1' mt='10px' mb='10px'></Box>

          <StyledDetailInfo>
            <StyledTitleDetail>APR</StyledTitleDetail>
            <Box fontSize='16px' fontWeight='700' color='white'>
              {option.rate}%
            </Box>
          </StyledDetailInfo>
          <StyledDetailInfo>
            <div>
              Estimated Interests
            </div>
            <div className='value'>
              {calculateRate().toFixed(4)} {packageItem.tokenName}
            </div>
          </StyledDetailInfo>
        </StyledLeft>
        <StyledRight className='stake-info'>
          <Flex justify='space-between' align='center'>
            <Box fontSize='22px' fontWeight='700' color='#f3ff08'>
              STAKE
            </Box>
            {
              packageItem.contractTokenPair2 ? <div>
                <Link href={`${rootUrlsView.addLP}${packageItem.contractTokenPair1}/${packageItem.contractTokenPair2}`} isExternal>
              <div style={{display: 'flex'}}>
                <StyledBuyWana>Get {packageItem.tokenName} LP</StyledBuyWana>

                <Image src='/icon/icon-buy.svg' alt='icon-buy' />
              </div>
            </Link>
            <Link href={`${rootUrlsView.seePairInfo}${packageItem.contractToken}`} isExternal>
              <div style={{display: 'flex'}}>
                <StyledBuyWana>See Pair Info</StyledBuyWana>

                <Image src='/icon/icon-buy.svg' alt='icon-buy' />
              </div>
            </Link>
              </div> : <Link href={`${rootUrlsView.swap}${packageItem.contractToken}`} isExternal>
              <div style={{display: 'flex'}}>
                <StyledBuyWana>Buy {packageItem.tokenName}</StyledBuyWana>

                <Image src='/icon/icon-buy.svg' alt='icon-info' />
              </div>
            </Link>
            }
          </Flex>
          <Flex justify='space-between' alignItems='center' mt='10px'>
            <div>Amount</div>
            <div>Available: {numberFormat(parseBalance(packageItem.balanceLP, 18), 2)} {packageItem.tokenName}</div>
          </Flex>
          <Box
            borderRadius={4}
            mt='5px'
            p='5px 10px'
            border={
              packageItem.balanceLP < packageItem.minStake || disableConfirm
                ? '1px solid #ee2737'
                : '1px solid #1a876a'
            }
          >
            <Flex justify='space-between' align='center'>
              <StyledNumberFormat
                value={stakeAmount}
                min={packageItem.minStake}
                max={packageItem.maxStake}
                thousandSeparator={true}
                decimalScale={4}
                allowNegative={false}
                disabled={isLoading || checkDisabled()}
                onValueChange={(values) => handleStakeAmount(values)}
              />
              <Flex justify='center' align='center'>
                <Box as='span' fontWeight='600' fontSize='20px' whiteSpace="nowrap">
                  {packageItem.tokenName}
                </Box>
                <Box
                  padding='5px 10px'
                  fontWeight='600'
                  fontSize='16px'
                  color='#fff'
                  background='linear-gradient(to right,#00aeef,#2b3990)'
                  borderRadius='5px'
                  cursor='pointer'
                  ml={4}
                  onClick={handleMaxWANA}
                >
                  Max
                </Box>
              </Flex>
            </Flex>
          </Box>

          {errorMessage && (
            <StyledErrorMessage>
              {errorMessage}
            </StyledErrorMessage>
          )}

          {warningMessage && (
            <StyledErrorMessage>
              {warningMessage}
            </StyledErrorMessage>
          )}

          {packageItem.balanceLP < packageItem.minStake && (
            <StyledErrorMessage>
              {`The available amount can not be lower than ${numberFormat(
                packageItem.minStake
              )} WANA`}
            </StyledErrorMessage>
          )}

          {transactionInfo.transactionHash && (
            <Box mt='10px'>
              <AlertHashTransaction
                transactionInfo={transactionInfo}
                closeTransactionHash={closeTransactionHash}
              />
            </Box>
          )}
          <Button
            type='primary'
            className='btn-stake'
            size='large'
            loading={isLoading}
            disabled={checkDisabled() || disableConfirm || isLoading || checkOverPoolLimit() || errorMessage}
            onClick={handleConfirmStake}
          >
            {allowanceAmount < stakeAmount ? 'Approve & Stake' : 'Stake'}
          </Button>
        </StyledRight>
      </StyledContentDetail>
    </StyledPackedDetail>
  );
}
